import * as React from "react"
import { useMediaQuery } from "../lib/hooks/use-media-query"
import { cn } from "../lib/utils"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./dialog"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "./drawer"
import { useModalSticky } from "./hooks/useModalSticky"

export interface ResponsiveModalLayoutProps {
  children: React.ReactNode
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  description?: string
  className?: string
  stickyHeader?: boolean
  dismissible?: boolean
}

/**
 * Viewport-aware Dialog/Drawer layout component.
 * Extracted from responsive-modal.tsx to centralize all
 * viewport/sticky/dark-mode header className logic.
 */
export function ResponsiveModalLayout({
  children,
  open,
  onOpenChange,
  title,
  description,
  className,
  stickyHeader = false,
  dismissible = true,
}: ResponsiveModalLayoutProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)")
  // Detect caller-owns-layout cases (p-0, p-1, p-2) — header must still apply its own padding.
  // Token-aware check: split on whitespace to avoid false positives like p-12, p-16, p-20.
  const P0_CLASSES = new Set(['p-0', 'p-1', 'p-2'])
  const isP0 = className ? className.split(/\s+/).some((cls) => P0_CLASSES.has(cls)) : false
  // Strip max-w classes from className for mobile drawer (drawer is always full-width)
  const drawerClassName = className?.replace(/max-w-\S+/g, '').trim()

  const headerClassName = useModalSticky({ stickyHeader, isP0, isDesktop })

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={cn(className)}>
          <DialogHeader className={headerClassName}>
            {title && <DialogTitle>{title}</DialogTitle>}
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
          {children}
        </DialogContent>
      </Dialog>
    )
  }

  // Mobile (Drawer)
  return (
    <Drawer open={open} onOpenChange={onOpenChange} dismissible={dismissible}>
      <DrawerContent className={cn(drawerClassName, isP0 && "!left-3 !right-3 !bottom-3 rounded-t-3xl !mt-0 [&>div:first-child]:hidden")}>
        <DrawerHeader className={headerClassName}>
          {title && <DrawerTitle>{title}</DrawerTitle>}
          {description && <DrawerDescription>{description}</DrawerDescription>}
        </DrawerHeader>

        {isP0 ? (
          // Caller provides its own full layout (e.g. modals with internal flex + max-h + sticky content)
          children
        ) : (
          <div className="px-4 pb-8 overflow-y-auto">
            {children}
          </div>
        )}
      </DrawerContent>
    </Drawer>
  )
}
